import React, { FC, useRef } from 'react';
import { useIntl } from 'react-intl';
import { InView } from 'react-intersection-observer';
import clsx from 'clsx';
import { useStoreToggle } from '@printdeal/feature-management/react';
import { getConfiguration } from '../../../helpers/Configuration';
import type { IProductPage } from '../../../types/product/productDetailPage';
import Recommendations from '../../recommendations/Recommendations';
import { scrollToRef } from '../helpers';
import { handleRecentViewedProductOnClick, handleOnScrollCueClick } from '../Homepage.events';
import ScrollCue from '../scroll-cue/ScrollCue';
import { handleRecommendationProductImpressions } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { useCTRecommendations } from '../../recommendations/hooks/useCTRecommendations';
import { HomepageSectionProps } from '../types';

export interface RecommendationsSectionProps {
  section?: HomepageSectionProps;
  scrollCuesInView: boolean;
  setScrollCuesInView: (inView: boolean) => void;
}

export const RecommendationsSection: FC<RecommendationsSectionProps> = ({
  section,
  scrollCuesInView,
  setScrollCuesInView,
}) => {
  const intl = useIntl();
  const configuration = getConfiguration();

  const isImpressto = useStoreToggle('impressto-fr').featureToggle.isEnabled;
  const productSectionClass = clsx(isImpressto && 'odd:pd-bg-dim-100 even:pd-bg-white');
  // Refs to point to the section nodes
  const recommendationsSectionNode = useRef<HTMLDivElement | null>(null);

  /**
   * Get data from CT personalization
   * Placement Id: PLC-10000001
   */
  const {
    consentData, data: recentlyViewedHome, config, isPlacementLoaded, anonymousCOAMToken,
  } = useCTRecommendations('PLC-10000001', 'Home Page Section 2', null, 12000);

  const isScrollCueInView = (inView: boolean) => {
    if (scrollCuesInView) return;
    setScrollCuesInView(inView);
  };

  const scrollTeaserTitle = intl.formatMessage(
    { id: 'general.scrollTeaser.title', defaultMessage: 'Continue scrolling' },
  );

  return (
    <div data-testid="recommendations-section">
      {(configuration?.recommendationsConfig?.enabled && recentlyViewedHome) && (
        <div data-testid="recently-viewed-products">
          <Recommendations
            componentName="Home Page Section 2"
            itemsPerRow={4}
            config={config}
            consentData={consentData}
            data={recentlyViewedHome}
            isPlacementLoaded={isPlacementLoaded}
            anonymousCOAMToken={anonymousCOAMToken}
            clickHandler={handleRecentViewedProductOnClick}
            onView={
            (products: IProductPage[], analyticsId: string) => {
              handleRecommendationProductImpressions('homepage_recentproducts', products, analyticsId);
            }
          }
            className={productSectionClass}
            sectionVariant={section?.backgroundStyle || 'white'}
            showHeader
          >
            <InView onChange={isScrollCueInView}>
              <ScrollCue
                title={scrollTeaserTitle}
                onCueClick={() => {
                  scrollToRef(recommendationsSectionNode);
                  handleOnScrollCueClick('recommendations');
                }}
              />
              <div className="scroll-anchor" ref={recommendationsSectionNode} />
            </InView>
          </Recommendations>
        </div>
      )}
    </div>
  );
};
